import { Link, NavLink } from 'react-router-dom';
import {
  Activity,
  Bell,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  CircleDollarSign,
  Headphones,
  LayoutDashboard,
  Mail,
  Phone,
  Settings,
  Ticket,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { NavigationGroup, NavigationIconId } from '../app/navigation';
import type { UiShellVersion } from '../app/runtime-config';
import { MobileNavigation } from './MobileNavigation';
import { BrandLogo } from './BrandLogo';

export interface SidebarProps {
  version: UiShellVersion;
  groups: readonly NavigationGroup[];
  pathname: string;
  theme: 'light' | 'dark';
  collapsed: boolean;
  mobileOpen: boolean;
  onNavigate: () => void;
  appVersion: string;
}

const ICONS: Readonly<Record<NavigationIconId, LucideIcon>> = {
  home: LayoutDashboard,
  'contact-center': Headphones,
  phone: Phone,
  monitoring: Activity,
  tickets: Ticket,
  companies: Building2,
  contacts: Users,
  templates: BookOpen,
  'planned-works': CalendarClock,
  visits: BriefcaseBusiness,
  mail: Mail,
  billing: CircleDollarSign,
  notifications: Bell,
  settings: Settings,
  admin: Settings,
};

function isActivePath(pathname: string, path: string, end: boolean) {
  if (end || path === '/') return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function Sidebar({
  version,
  groups,
  pathname,
  theme,
  collapsed,
  mobileOpen,
  onNavigate,
  appVersion,
}: SidebarProps) {
  const directoryActive = pathname === '/companies'
    || pathname === '/contacts'
    || pathname === '/templates'
    || pathname.startsWith('/templates/');

  const itemIsActive = (item: NavigationGroup['items'][number]) => (
    item.path === '/companies' && directoryActive
      ? true
      : isActivePath(pathname, item.path, item.end)
  );

  const renderLegacyLink = (item: NavigationGroup['items'][number]) => {
    const Icon = ICONS[item.icon];
    const active = itemIsActive(item);
    return <Link
      key={item.path}
      to={item.path}
      className={active ? 'active' : undefined}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      title={item.label}
      onClick={onNavigate}
    >
      <Icon aria-hidden="true" />
      <span>{item.label}</span>
    </Link>;
  };

  if (version === 'legacy') {
    const items = groups.flatMap(group => group.items);

    return <>
      {mobileOpen && <button className="sidebar-overlay" type="button" onClick={onNavigate} aria-label="Закрыть меню" />}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`} aria-label="Основная навигация">
        <div className="sidebar-head">
          {mobileOpen && <button
            type="button"
            className="sidebar-toggle"
            onClick={onNavigate}
            aria-label="Закрыть боковое меню"
            title="Закрыть меню"
          >
            <X />
          </button>}
          <NavLink className="sidebar-brand sidebar-mobile-brand" to="/" onClick={onNavigate} aria-label="Перейти на главную страницу">
            <img src="/brand/intez-helpdesk-capital.png" alt="Intez Group" />
          </NavLink>
        </div>
        <nav onClick={onNavigate} aria-label="Разделы портала">
          {items.map(renderLegacyLink)}
        </nav>
        <div className="sidebar-footer">
          <small>Intez Helpdesk v{appVersion}</small>
        </div>
      </aside>
    </>;
  }

  const items = groups.flatMap(group => group.items);

  return (
    <MobileNavigation open={mobileOpen} onClose={onNavigate}>
      <aside
        className={`sidebar ihd-sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'collapsed ihd-sidebar--collapsed' : ''}`}
        aria-label="Основная навигация"
        data-sidebar-collapsed={collapsed || undefined}
        data-shell-version={version}
      >
        <div className="sidebar-head ihd-sidebar__head">
          <NavLink className="sidebar-brand ihd-sidebar__brand" to="/" onClick={onNavigate} aria-label="Intez Group Helpdesk — на главную">
            <BrandLogo theme={theme} />
          </NavLink>
          {mobileOpen && <button
            type="button"
            className="sidebar-toggle ihd-sidebar__toggle"
            onClick={onNavigate}
            aria-label="Закрыть боковое меню"
            title="Закрыть меню"
          >
            <X />
          </button>}
        </div>
        <nav className="ihd-sidebar__navigation" aria-label="Разделы портала">
          <div className="ihd-navigation-list">
            {items.map(item => {
              const Icon = ICONS[item.icon];
              const active = itemIsActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={active ? 'active' : undefined}
                  aria-current={active ? 'page' : undefined}
                  aria-label={item.label}
                  title={item.label}
                  onClick={onNavigate}
                >
                  <span className="ihd-navigation-list__icon" aria-hidden="true"><Icon /></span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
        <div className="sidebar-footer ihd-sidebar__footer">
          <small>Intez Helpdesk v{appVersion}</small>
        </div>
      </aside>
    </MobileNavigation>
  );
}
